import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';

import api from '../../services/api';
import CustomerFormFields from './customer-form-fields';
import CustomerInvestments from './investments/customer-investments';

const CustomerDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [fieldsToUpdate, setFieldsToUpdate] = useState<any>({});
  const [selectedBeneficiaryIndex, setSelectedBeneficiaryIndex] = useState<number | null>(null);
  const [investments, setInvestments] = useState<any[]>([]);

  const serializeBeneficiaries = (beneficiaries: any) => {
    return beneficiaries.map((beneficiary: any) => {
      const { beneficiaryProfile, beneficiaryDocuments, beneficiaryBanks, profileBeneficiaryId } =
        beneficiary;
      return {
        ...beneficiaryProfile,
        profileBeneficiaryId,
        ...beneficiaryBanks,
        documents: Array.isArray(beneficiaryDocuments)
          ? beneficiaryDocuments
          : [beneficiaryDocuments],
      };
    });
  };

  const serializeProfile = useCallback((profile: any) => {
    const { beneficiaries, banks, ...profileData } = profile;
    const parsedBeneficiaries = serializeBeneficiaries(beneficiaries);
    return {
      ...profileData,
      bankId: banks[0].bankId,
      clabe: banks[0].clabe,
      beneficiaries: parsedBeneficiaries,
    };
  }, []);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const response = await api.get(`/users/${id}/profile`);
        const parsedResponse = serializeProfile(response.data);
        setCustomer(parsedResponse);
      } catch (error) {
        console.error('Error obteniendo el cliente:', error);
      }
    };

    const fetchInvestments = async () => {
      try {
        const responseInvestments = await api.get(`/users/${id}/investments`);
        setInvestments(responseInvestments.data);
      } catch (error) {
        console.error('Error obteniendo las inversiones:', error); 
      }
    }

    if (id) {
      fetchCustomer();
      fetchInvestments();
      setLoading(false);
    }
  }, [id, serializeProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { beneficiaries, ...customerData } = fieldsToUpdate;
      if (customerData.phone) {
        customerData.phone = customerData.phone.replace(new RegExp('-', 'g'), () => '');
      }
      await api.patch(`/users/${id}/profile`, customerData);
      alert('Cliente actualizado correctamente');
    } catch (error: any) {
      console.error('Error al actualizar el cliente:', error);
      
      // Extract specific error message from the API response
      let errorMessage = 'Ocurrió un error al actualizar datos del cliente.';
      
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const validateBeneficiaryDataUpdated = (beneficiaryData: any) => {
    const { documents, ...beneficiary } = beneficiaryData;
    const validatedDocuments = documents.filter((doc: any) => doc.file);

    return {
      ...beneficiary,
      documents: validatedDocuments,
    };
  };

  const handleSubmitBeneficiary = async () => {
    const { beneficiaries } = fieldsToUpdate;
    let beneficiaryData;
    if (selectedBeneficiaryIndex !== null) {
      beneficiaryData = beneficiaries[selectedBeneficiaryIndex];
    }
    try {
      if (beneficiaryData.profileBeneficiaryId) {
        beneficiaryData = validateBeneficiaryDataUpdated(beneficiaryData);
        await api.patch(
          `/users/${id}/beneficiaries/${beneficiaryData.profileBeneficiaryId}`,
          beneficiaryData,
        );
      } else {
        await api.post(`/users/${id}/beneficiaries`, { beneficiaries: [beneficiaryData] });
      }
    } catch (error: any) {
      console.error('Error al procesar beneficiarios:', error);
      
      // Extract specific error message from the API response
      let errorMessage = 'Ocurrió un error en el proceso de beneficiarios.';
      
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (selectedBeneficiaryIndex !== null) {
      // Asegurar que beneficiaries existe
      const updatedBeneficiaries = Array.isArray(fieldsToUpdate.beneficiaries)
        ? [...fieldsToUpdate.beneficiaries]
        : [...customer.beneficiaries];

      if (!updatedBeneficiaries[selectedBeneficiaryIndex]) {
        updatedBeneficiaries[selectedBeneficiaryIndex] = {};
      }

      // Actualizar el beneficiario
      updatedBeneficiaries[selectedBeneficiaryIndex] = {
        ...updatedBeneficiaries[selectedBeneficiaryIndex],
        [target.name]: target.value,
      };
      customer.beneficiaries[selectedBeneficiaryIndex] =
        updatedBeneficiaries[selectedBeneficiaryIndex];

      setCustomer(customer);
      setFieldsToUpdate({ ...fieldsToUpdate, beneficiaries: updatedBeneficiaries });
    } else {
      setCustomer({ ...customer, [target.name]: target.value });
      setFieldsToUpdate({ ...fieldsToUpdate, [target.name]: target.value });
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<any>,
    documentTypeId: number,
    index: number | null,
  ) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      const file = files[0];

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];
        setFieldsToUpdate((prevData: any) => {
          if (index !== null) {
            const updatedBeneficiaries = Array.isArray(fieldsToUpdate?.beneficiaries)
              ? [...fieldsToUpdate.beneficiaries]
              : [];

            if (!updatedBeneficiaries[index]) {
              const profileBeneficiaryId = customer.beneficiaries[index].profileBeneficiaryId;
              updatedBeneficiaries[index] = { profileBeneficiaryId, documents: [] };
            }

            const currentBeneficiary = updatedBeneficiaries[index];

            updatedBeneficiaries[index] = {
              ...currentBeneficiary,
              documents: Array.isArray(currentBeneficiary?.documents)
                ? [
                    ...currentBeneficiary.documents.filter(
                      (doc: any) => doc.documentTypeId !== documentTypeId,
                    ),
                    {
                      documentTypeId,
                      filename: file.name,
                      file: base64String,
                    },
                  ]
                : [{ documentTypeId, filename: file.name, file: base64String }],
            };

            return {
              ...prevData,
              beneficiaries: updatedBeneficiaries,
            };
          } else {
            return {
              ...prevData,
              documents: [
                ...(prevData?.documents ?? []).filter(
                  (doc: any) => doc.documentTypeId !== documentTypeId,
                ),
                {
                  documentTypeId,
                  filename: file.name,
                  file: base64String,
                },
              ],
            };
          }
        });
      };
    }
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <Row className="mt-3 sticky-header">
          <Col md={2}>
            <Button variant="secondary" onClick={() => navigate('/dashboard/customers')}>
              Regresar
            </Button>
          </Col>
          <Col className="text-center" md={8}>
            <h2>Detalle de cliente</h2>
          </Col>
          <Col md={2}>
            <Button variant="primary" onClick={handleSubmit}>
              Guardar
            </Button>
          </Col>
        </Row>

        <Row className="mt-4">
          {loading ? (
            <Spinner animation="border" />
          ) : customer && id ? (
            <Container>
              <CustomerFormFields
                formData={customer}
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                handleBeneficiarySubmit={handleSubmitBeneficiary}
                isEdit={true}
                setSelectedBeneficiaryIndex={setSelectedBeneficiaryIndex}
              />
              <CustomerInvestments investments={investments} userId={id} />
            </Container>
          ) : (
            <p>No se encontró el cliente.</p>
          )}
        </Row>
      </Form>
    </Container>
  );
};

export default CustomerDetails;
